import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2, MapPin, ShieldCheck } from "lucide-react";
import { PageShell, PageHero } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { getIndiaLocation, INDIA_LOCATION_PAGES, type IndiaLocation } from "@/lib/locations";
import { SITE_ORIGIN, sectionImageMeta, SECTION_OG } from "@/lib/ogSections";
import { hreflangLinks } from "@/lib/hreflang";
import { pageSchemaScript, PERSON_ID } from "@/seo/schema";

export const Route = createFileRoute("/locations/$slug")({
  loader: ({ params }) => {
    const location = getIndiaLocation(params.slug);
    if (!location) throw notFound();
    return location;
  },
  component: LocationPage,
  head: ({ loaderData }: { loaderData?: IndiaLocation }) => {
    if (!loaderData) {
      return { meta: [{ title: "Location not found | Arif Khan" }, { name: "robots", content: "noindex" }] };
    }
    const url = `${SITE_ORIGIN}/locations/${loaderData.slug}`;
    const title = loaderData.metaTitle;
    const description = loaderData.metaDescription;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        ...sectionImageMeta(SECTION_OG.industries, `${loaderData.name} location planning by Arif Khan`),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks(`/locations/${loaderData.slug}`)],
      scripts: [
        pageSchemaScript(
          {
            path: `/locations/${loaderData.slug}`,
            type: "WebPage",
            name: title,
            description,
            breadcrumb: [
              { name: "Locations", url: "/locations" },
              { name: loaderData.name, url: `/locations/${loaderData.slug}` },
            ],
            extra: { about: { "@id": PERSON_ID } },
          },
          [
            {
              "@type": "Service",
              "@id": `${url}#service`,
              name: loaderData.headline,
              serviceType: "Security Infrastructure & Building Technology Consultancy",
              description,
              url,
              provider: { "@id": PERSON_ID },
              areaServed: { "@type": loaderData.kind === "city" ? "City" : "State", name: loaderData.name },
              serviceOutput: loaderData.focus,
            },
          ],
        ),
      ],
    };
  },
});

function LocationPage() {
  const location = Route.useLoaderData();
  const related = INDIA_LOCATION_PAGES.filter((item) => item.slug !== location.slug && item.kind === location.kind).slice(0, 4);

  return (
    <PageShell>
      <PageHero
        eyebrow={`${location.region} · Locations`}
        title={location.headline}
        description={location.summary}
      />

      <section className="py-20 border-b border-border">
        <div className="container-page grid gap-12 lg:grid-cols-[1.1fr_0.9fr]">
          <div>
            <div className="flex items-center gap-2 text-ink-soft">
              <MapPin className="h-4 w-4 text-primary" />
              <span className="eyebrow">Built around the site</span>
            </div>
            <h2 className="mt-5 text-3xl md:text-4xl font-semibold tracking-tight">Technology planning that starts with risk, operations and architecture.</h2>
            <p className="mt-5 max-w-2xl text-lg text-ink-soft leading-relaxed">
              Projects in {location.name} need a coherent brief before cameras, readers, barriers, displays or control platforms are selected. The work connects the security system to the way the property is designed, occupied and operated.
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-surface p-7">
            <ShieldCheck className="h-6 w-6 text-primary" strokeWidth={1.5} />
            <h2 className="mt-5 text-xl font-semibold">Areas of focus</h2>
            <ul className="mt-5 space-y-4">
              {location.focus.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-ink-soft">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-20 bg-surface border-b border-border">
        <div className="container-page">
          <div className="max-w-3xl">
            <span className="eyebrow">What the engagement covers</span>
            <h2 className="mt-5 text-3xl md:text-4xl font-semibold tracking-tight">A vendor-neutral brief for the full technology stack.</h2>
          </div>
          <div className="mt-12 grid gap-px overflow-hidden rounded-2xl border border-border bg-border md:grid-cols-3">
            {location.planning.map((item, index) => (
              <Reveal key={item} delay={index * 0.06}>
                <div className="h-full bg-background p-7">
                  <div className="font-mono text-xs text-primary">0{index + 1}</div>
                  <p className="mt-6 text-base font-medium leading-relaxed">{item}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container-page grid gap-12 lg:grid-cols-2">
          <div>
            <span className="eyebrow">Project contexts</span>
            <h2 className="mt-5 text-3xl font-semibold tracking-tight">Designed for the environments that shape {location.name}.</h2>
            <div className="mt-8 flex flex-wrap gap-3">
              {location.sectors.map((sector) => <span key={sector} className="rounded-full border border-border px-4 py-2 text-sm text-ink-soft">{sector}</span>)}
            </div>
          </div>
          <div className="rounded-2xl border border-border p-7">
            <h2 className="text-xl font-semibold">Start with the right brief</h2>
            <p className="mt-3 text-sm leading-relaxed text-ink-soft">Share your site, project stage and operating challenge. We will turn the first conversation into a clear next step.</p>
            <Link to="/contact" className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-foreground transition-colors">Let's Talk <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="py-20 bg-surface border-t border-border">
          <div className="container-page">
            <div className="flex items-end justify-between gap-6">
              <div><span className="eyebrow">Explore locations</span><h2 className="mt-4 text-3xl font-semibold tracking-tight">More coverage across India.</h2></div>
              <Link to="/locations" className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-primary">All locations <ArrowRight className="h-4 w-4" /></Link>
            </div>
            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {related.map((item) => <Link key={item.slug} to="/locations/$slug" params={{ slug: item.slug }} className="rounded-xl border border-border bg-background p-5 hover:border-border-strong transition-colors"><span className="text-base font-semibold">{item.name}</span><span className="mt-2 block text-xs text-ink-soft">{item.headline}</span></Link>)}
            </div>
          </div>
        </section>
      )}
    </PageShell>
  );
}
